import React, { FC, useMemo, useState } from 'react';
import { Card, Modal, Select } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import {
	getSectionBucketCounts,
	getSectionSortOptions,
	SECTION_SORT_MODE,
	sortWalkthroughSectionRows,
	WalkthroughSectionRow,
} from '../../utils/WalkthroughComparisonUtils';
import WalkthroughSectionScoreChart, {
	getFittedSectionChartHeight,
	getSectionChartHeight,
} from './WalkthroughSectionScoreChart';
import useElementSize from '../../hooks/useElementSize';
import { cn } from '../../utils/ClassNamesUtils';

const SORT_SELECT_WIDTH_PX = 200;

const EXPANDED_MODAL_WIDTH = '92vw';

const BucketLegend: FC<{ rows: any[] }> = ({ rows }) => {
	const bucketCounts = getSectionBucketCounts(rows);
	return (
		<div className="flex flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2">
			{bucketCounts.map((bucketCount) => (
				<div key={bucketCount.bucket} className="flex flex-row items-center gap-2">
					<span
						className="inline-block h-2 w-2 rounded-full"
						style={{ backgroundColor: bucketCount.color }}
					/>
					<span className="footnoteregular text-character-secondary">{bucketCount.label}</span>
					<span className="footnoteregular text-character-primary">
						{`${bucketCount.percentage}% (${bucketCount.count}/${bucketCount.total})`}
					</span>
				</div>
			))}
		</div>
	);
};

interface WalkthroughScoreBreakdownCardProps {
	sectionRows: WalkthroughSectionRow[];
	isUnscored: boolean;
	className?: string;
	maxHeight?: number;
}

export const WalkthroughScoreBreakdownCard: FC<WalkthroughScoreBreakdownCardProps> = ({
	sectionRows,
	isUnscored,
	className,
	maxHeight,
}) => {
	const [sortMode, setSortMode] = useState(SECTION_SORT_MODE.WALKTHROUGH);
	const [expanded, setExpanded] = useState(false);
	const [chartAreaRef, { height: chartAreaHeight }] = useElementSize();

	const sortedRows = useMemo(
		() =>
			sortWalkthroughSectionRows(
				sectionRows,
				sortMode,
				isUnscored ? 'completionPercentage' : 'percentageScore'
			),
		[sectionRows, sortMode, isUnscored]
	);

	// A single section has nothing to break down.
	if (sectionRows.length <= 1) return null;

	const inlineChartHeight = getFittedSectionChartHeight(sortedRows.length, chartAreaHeight);
	const modalChartHeight = getSectionChartHeight(sortedRows.length);

	const openExpanded = () => setExpanded(true);
	const closeExpanded = () => setExpanded(false);

	const cardTitle = isUnscored ? 'Section Completion' : 'Score Breakdown by Section';
	const sortOptions = getSectionSortOptions(isUnscored);

	const cardExtra = (
		<div className="flex flex-row items-center gap-3">
			<Select
				value={sortMode}
				onChange={setSortMode}
				options={sortOptions}
				style={{ width: SORT_SELECT_WIDTH_PX }}
				aria-label="Sort sections"
			/>
			<FullscreenOutlined
				className="cursor-pointer text-character-secondary"
				onClick={openExpanded}
				aria-label="Expand score breakdown"
			/>
		</div>
	);

	const cardChart = (
		<div ref={chartAreaRef} className="min-h-0 w-full flex-1 overflow-y-auto">
			<WalkthroughSectionScoreChart
				rows={sortedRows}
				height={inlineChartHeight}
				unscored={isUnscored}
			/>
		</div>
	);

	const modalChart = (
		<div className="min-h-0 w-full flex-1">
			<WalkthroughSectionScoreChart
				rows={sortedRows}
				height={modalChartHeight}
				fitContainerHeight={true}
				unscored={isUnscored}
			/>
		</div>
	);

	const legend = isUnscored ? null : (
		<div className="mt-3 shrink-0 border-0 border-t border-solid border-grey-4 pt-3">
			<BucketLegend rows={sortedRows} />
		</div>
	);

	return (
		<Card
			title={cardTitle}
			extra={cardExtra}
			className={cn(
				className,
				'flex flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col [&_.ant-card-body]:overflow-hidden'
			)}
			style={{ maxHeight }}
		>
			{cardChart}
			{legend}
			{expanded ? (
				<Modal
					title={cardTitle}
					visible={expanded}
					onCancel={closeExpanded}
					footer={null}
					width={EXPANDED_MODAL_WIDTH}
					centered={true}
					className="max-w-[1600px] !pb-0 [&_.ant-modal-body]:flex [&_.ant-modal-body]:min-h-0 [&_.ant-modal-body]:flex-auto [&_.ant-modal-body]:flex-col [&_.ant-modal-body]:overflow-hidden [&_.ant-modal-content]:flex [&_.ant-modal-content]:h-[88vh] [&_.ant-modal-content]:flex-col"
					destroyOnClose={true}
				>
					<div className="mb-3 flex shrink-0 flex-row items-center justify-end gap-3">
						<Select
							value={sortMode}
							onChange={setSortMode}
							options={sortOptions}
							style={{ width: SORT_SELECT_WIDTH_PX }}
							aria-label="Sort sections"
						/>
					</div>
					{modalChart}
					{legend}
				</Modal>
			) : null}
		</Card>
	);
};

export default WalkthroughScoreBreakdownCard;
